import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { taxPaymentsGateway } from '../index';
import { ApiError } from '../../identity/types';
import { StatusBadge } from '../components/StatusBadge';
import { formLabels } from '../types';
import type { DashboardDetail, DashboardRow, TaxPaymentScope } from '../dashboardTypes';
import { DASH, formatDay, formatMoney } from '../format';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { AuditTab } from './AuditTab';
import { DetailsTab } from './DetailsTab';
import { OwnershipTab } from './OwnershipTab';
import { describeFailure } from './failures';
import { footerFor, TAX_SETTINGS_PATH, type FooterItem } from './footerActions';
import { bannerFor } from './statusCopy';
import { MarkInsufficientFundsDialog } from '../dialogs/MarkInsufficientFundsDialog';
import { OutsidePaymentSuccessDialog } from '../dialogs/OutsidePaymentSuccessDialog';
import { RecordOutsidePaymentDialog } from '../dialogs/RecordOutsidePaymentDialog';
import { RetryPaymentDialog } from '../dialogs/RetryPaymentDialog';
import { ViewPaymentRecordDialog } from '../dialogs/ViewPaymentRecordDialog';
import { VoidOutsidePaymentDialog } from '../dialogs/VoidOutsidePaymentDialog';

type TabKey = 'details' | 'ownership' | 'audit';
const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'details', label: 'Payment Details' },
  { key: 'ownership', label: 'Approval and Ownership' },
  { key: 'audit', label: 'Audit History' },
];

type Dialog =
  | { kind: 'record' } | { kind: 'view' } | { kind: 'void' } | { kind: 'markInsufficientFunds' } | { kind: 'retry' }
  | { kind: 'recorded'; detail: DashboardDetail }
  | null;

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The right-hand payment drawer (Figma S10 to S13, S23 to S26, S33 to S37, S39 to S44), 640px. The header (payment, status, amounts, banner and tabs) and the footer
 * stay in place; only the body scrolls. Everything shown comes from `GET .../dashboard` for this payment; the footer is what the SERVER offers (see `footerFor`).
 * Nothing changes on screen before the server answers: every write replaces the drawer's data with the server's response and refreshes the list, cards and queue.
 * Escape and an outside click close the drawer unless a dialog is open or there is an unsent note (then it asks first). Focus moves in, is kept inside, and returns to
 * the control that opened it.
 */
export function PaymentDrawer({ paymentId, initialRow, scope, readOnly, companyName, onClose, onChanged }: {
  paymentId: string;
  initialRow: DashboardRow | null;
  scope: TaxPaymentScope;
  readOnly: boolean;
  companyName: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const uid = useId();
  const asideRef = useRef<HTMLElement>(null);
  const openerRef = useRef<Element | null>(typeof document === 'undefined' ? null : document.activeElement);
  const [detail, setDetail] = useState<DashboardDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<TabKey>('details');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [simulationKey, setSimulationKey] = useState(0);
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const dialogOpen = dialog !== null;

  // ---- load: the drawer's own endpoint (company door, or the platform door for a Super Admin) -----------------------------------------------
  useEffect(() => {
    const controller = new AbortController();
    taxPaymentsGateway.getDashboardDetail(paymentId, scope, controller.signal)
      .then((d) => { if (!controller.signal.aborted) { setDetail(d); setLoadError(null); } })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(err instanceof ApiError && err.status === 404 ? 'That payment could not be found.' : friendlyErrorMessage(err, 'Could not open this payment.'));
      });
    return () => controller.abort();
  }, [paymentId, scope, reloadKey]);

  const reload = useCallback(() => { setLoadError(null); setReloadKey((k) => k + 1); }, []);

  /** The server's answer to a write replaces what is shown, and the list, cards and queue are re-read. Never an optimistic change. */
  const apply = useCallback((updated: DashboardDetail) => {
    setDetail(updated);
    setSimulationKey((k) => k + 1);
    onChanged();
  }, [onChanged]);

  // ---- focus: in on open, kept inside, back to the opener on close ---------------------------------------------------------------------------
  useEffect(() => {
    const opener = openerRef.current;
    asideRef.current?.focus();
    return () => { if (opener instanceof HTMLElement && document.contains(opener)) opener.focus(); };
  }, []);

  /** Dismissing the discard question puts focus back in the note being written, so the keyboard is never left on a control that has just disappeared. */
  const keepEditing = () => {
    setConfirmDiscard(false);
    window.setTimeout(() => (asideRef.current?.querySelector<HTMLElement>('textarea') ?? asideRef.current)?.focus(), 0);
  };

  const requestClose = useCallback(() => {
    if (dialogOpen) return;
    if (hasDraft) { setConfirmDiscard(true); return; }
    onClose();
  }, [dialogOpen, hasDraft, onClose]);

  function onKeyDown(e: KeyboardEvent<HTMLElement>) {
    if (dialogOpen) return;
    if (e.key === 'Escape') { e.stopPropagation(); requestClose(); return; }
    if (e.key !== 'Tab') return;
    const nodes = Array.from(asideRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === asideRef.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }

  // ---- footer ------------------------------------------------------------------------------------------------------------------------------
  const footer = detail ? footerFor(detail, { scope, readOnly }) : [];

  function activate(item: FooterItem) {
    if (!item.enabled) return;
    switch (item.id) {
      case 'record': setDialog({ kind: 'record' }); break;
      case 'view': setDialog({ kind: 'view' }); break;
      case 'void': setDialog({ kind: 'void' }); break;
      case 'markInsufficientFunds': setDialog({ kind: 'markInsufficientFunds' }); break;
      case 'retry': setRetryError(null); setDialog({ kind: 'retry' }); break;
      case 'refresh': setNotice('Status refreshed from the server.'); reload(); break;
      default: break;
    }
  }

  async function retry() {
    setRetryBusy(true);
    setRetryError(null);
    try {
      await taxPaymentsGateway.act(paymentId, 'reissue');
      const fresh = await taxPaymentsGateway.getDashboardDetail(paymentId, scope);
      setDialog(null);
      setNotice('A new simulated attempt was created. Nothing was sent to a bank or agency.');
      apply(fresh);
    } catch (err) {
      setRetryError(describeFailure(err, 'The payment couldn’t be retried. Nothing was changed. Try again.').message);
    } finally {
      setRetryBusy(false);
    }
  }

  const row = detail?.row ?? initialRow;
  const form = row ? (formLabels[row.formCode] ?? row.formCode) : 'Tax payment';
  const title = companyName ?? form;
  const banner = detail ? bannerFor(detail) : null;
  const disabledReasons = footer.filter((f) => !f.enabled && f.reason);

  return (
    <div className="tpd-drawer-layer" data-testid="drawer-layer">
      <div className="tpd-backdrop" data-testid="drawer-backdrop" aria-hidden="true" onClick={requestClose} />
      <aside
        ref={asideRef} className="tpd-drawer tpd-drawer--wide" role="dialog" aria-modal="true" aria-label={`Payment details: ${title}`} aria-busy={!detail && !loadError}
        tabIndex={-1} onKeyDown={onKeyDown} data-testid="payment-drawer"
      >
        <header className="tpd-drawer__top">
          <div className="tpd-drawer__titlebar">
            <div>
              <h3 className="tpd-drawer__title">{title}</h3>
              <p className="tpd-muted" data-testid="drawer-subtitle">
                EIN <span title="EIN: not available yet">{DASH}<span className="tpd-visually-hidden"> EIN not available yet</span></span>
                {companyName ? ` · ${form}` : ''} · ID <span className="tpd-mono">{paymentId.slice(0, 8)}</span>
              </p>
            </div>
            <div className="tpd-drawer__status">
              {row && <StatusBadge status={row.status} />}
              <button type="button" className="tpd-iconbtn" onClick={requestClose} aria-label="Close payment details" disabled={dialogOpen}>×</button>
            </div>
          </div>

          {row && (
            <div className="tpd-amounts">
              <div className="tpd-amounts__main"><span className="tpd-amounts__label">Payment Amount</span><span className="tpd-amounts__value" data-testid="drawer-amount">{formatMoney(row.amount)}</span></div>
              <div><span className="tpd-amounts__label">Payment Due Date</span><span>{formatDay(row.dueDate)}</span><span className="tpd-sub">Legal due date</span></div>
              <div>
                <span className="tpd-amounts__label">Process No Later Than</span>
                <span title="Process No Later Than: not available yet">{DASH}<span className="tpd-visually-hidden"> Process No Later Than not available yet</span></span>
              </div>
            </div>
          )}

          {banner && (
            <div className={`tpd-banner tpd-banner--${banner.tone}`} role="note" data-testid="drawer-banner" data-tone={banner.tone}>
              <span className="tpd-banner__glyph" aria-hidden="true">{banner.tone === 'danger' ? '!' : banner.tone === 'warning' ? '!' : banner.tone === 'success' ? '✓' : 'i'}</span>
              <span>{banner.message}{banner.detail ? <span className="tpd-banner__detail"> {banner.detail}</span> : null}</span>
            </div>
          )}
          {detail?.simulationNote && <p className="tpd-muted tpd-simnote">{detail.simulationNote}</p>}
          {readOnly && <p className="tpd-muted" data-testid="drawer-read-only">Your plan is read only: you can view this payment, but not change it.</p>}

          <div className="tpd-drawer__tabs" role="tablist" aria-label="Payment sections">
            {TABS.map((t) => (
              <button
                key={t.key} id={`${uid}-tab-${t.key}`} type="button" role="tab" aria-selected={tab === t.key} aria-controls={`${uid}-panel`} tabIndex={tab === t.key ? 0 : -1}
                className={`tpd-dtab${tab === t.key ? ' is-active' : ''}`} onClick={() => setTab(t.key)}
                onKeyDown={(e) => {
                  const i = TABS.findIndex((x) => x.key === tab);
                  const next = e.key === 'ArrowRight' ? (i + 1) % TABS.length : e.key === 'ArrowLeft' ? (i - 1 + TABS.length) % TABS.length : e.key === 'Home' ? 0 : e.key === 'End' ? TABS.length - 1 : null;
                  if (next === null) return;
                  e.preventDefault();
                  e.stopPropagation();
                  setTab(TABS[next]!.key);
                  document.getElementById(`${uid}-tab-${TABS[next]!.key}`)?.focus();
                }}
              >{t.label}</button>
            ))}
          </div>
        </header>

        <div className="tpd-drawer__body" id={`${uid}-panel`} role="tabpanel" aria-labelledby={`${uid}-tab-${tab}`} tabIndex={0}>
          {notice && <p className="success" role="status" data-testid="drawer-notice">{notice}</p>}
          {!detail && !loadError && <p role="status">Loading…</p>}
          {loadError && (
            <div className="tpd-error" role="alert">
              <p>{loadError}</p>
              <button type="button" className="tpd-btn tpd-btn--outline" onClick={reload}>Try again</button>
            </div>
          )}
          {detail && tab === 'details' && (
            <DetailsTab
              detail={detail} showSimulation={scope === 'company'} simulationKey={simulationKey}
              onSimulationChanged={() => { reload(); onChanged(); }} onViewRecord={() => setDialog({ kind: 'view' })}
            />
          )}
          {detail && tab === 'ownership' && <OwnershipTab detail={detail} onSaved={apply} onDraftChange={setHasDraft} />}
          {detail && tab === 'audit' && <AuditTab detail={detail} />}
        </div>

        <footer className="tpd-drawer__foot" data-testid="drawer-footer">
          {confirmDiscard && (
            <div className="tpd-discard" role="alert">
              <span>You have an unsent note. Discard it and close?</span>
              <button type="button" className="tpd-btn tpd-btn--outline" autoFocus onClick={keepEditing}>Keep editing</button>
              <button type="button" className="tpd-btn tpd-btn--primary" onClick={onClose}>Discard and close</button>
            </div>
          )}
          <div className="tpd-foot-actions">
            {footer.map((item) => item.kind === 'link' ? (
              <a key={item.id} className="tpd-btn tpd-btn--outline tpd-btn--link" href={TAX_SETTINGS_PATH} target="_blank" rel="noopener noreferrer">
                {item.label}<span className="tpd-visually-hidden"> (opens in a new tab)</span>
              </a>
            ) : (
              <button
                key={item.id} type="button" data-action={item.id} aria-disabled={!item.enabled || undefined} aria-describedby={!item.enabled && item.reason ? `${uid}-why-${item.id}` : undefined}
                className={`tpd-btn tpd-btn--${item.tone === 'primary' ? 'primary' : item.tone === 'danger' ? 'danger' : 'outline'}${item.enabled ? '' : ' is-disabled'}`}
                onClick={() => activate(item)}
              >{item.label}</button>
            ))}
          </div>
          {disabledReasons.length > 0 && (
            <ul className="tpd-foot-reasons" data-testid="footer-reasons">
              {disabledReasons.map((f) => <li key={f.id} id={`${uid}-why-${f.id}`}><strong>{f.label}:</strong> {f.reason}</li>)}
            </ul>
          )}
        </footer>
      </aside>

      {detail && dialog?.kind === 'record' && (
        <RecordOutsidePaymentDialog detail={detail} companyName={companyName} onClose={() => setDialog(null)} onRecorded={(updated) => { apply(updated); setNotice(null); setDialog({ kind: 'recorded', detail: updated }); }} />
      )}
      {dialog?.kind === 'recorded' && <OutsidePaymentSuccessDialog detail={dialog.detail} onClose={() => setDialog(null)} />}
      {detail && dialog?.kind === 'view' && <ViewPaymentRecordDialog detail={detail} scope={scope} companyName={companyName} onClose={() => setDialog(null)} />}
      {detail && dialog?.kind === 'void' && (
        <VoidOutsidePaymentDialog detail={detail} companyName={companyName} onClose={() => setDialog(null)} onVoided={(updated) => { apply(updated); setNotice('The outside payment record was voided. It is kept in the audit history.'); setDialog(null); }} />
      )}
      {detail && dialog?.kind === 'markInsufficientFunds' && (
        <MarkInsufficientFundsDialog detail={detail} companyName={companyName} onClose={() => setDialog(null)} onRecorded={(updated) => { apply(updated); setNotice('Insufficient funds was recorded.'); setDialog(null); }} />
      )}
      {detail && dialog?.kind === 'retry' && <RetryPaymentDialog detail={detail} busy={retryBusy} error={retryError} onCancel={() => setDialog(null)} onConfirm={() => void retry()} />}
    </div>
  );
}
